import { PiClock, PiRocket } from "react-icons/pi";
import Input from "../../../components/inputs/input/input";
import InputDropdown from "../../../components/inputs/inputDropdown/inputDropdown";
import InputTextfield from "../../../components/inputs/inputTextfield/inputTextfield";
import InputDate from "../../../components/inputs/inputDate/inputDate";
import Modal from "../../../components/modal/modal";
import Table from "../../../components/table/table";
import { data } from "./data/data";
import Button from "../../../components/button/button";
import { IoAdd } from "react-icons/io5";
import Tabs from "../../../components/tabs/tabs";
import SectionHeader from "../../../components/sectionHeader/sectionHeader";
import { useState, useEffect } from "react";
import { LuEye } from "react-icons/lu";
import { FiEdit, FiTrash } from "react-icons/fi";
import { getApiInstance } from "../../../utils/api";
import { useBorradores } from "../../../utils/borradoresContext";
import Badge from "../../../components/badge/badge";

import "./versiones.scss";

const estadoLabels = {
    Publicado: "Publicado",
    EnEdicion: "En edición",
    Archivado: "Archivado",
    PendienteDeAprobacion: "Pendiente de aprobación",
    ListoParaPublicar: "Listo para publicar",
};

const estadoModificacionLabels = {
    1: "Pendiente",
    2: "Modificado",
    3: "Descatalogado",
    4: "Nuevo",
};

const familiaMapping = {
    1: "Mobiliario Central",
    2: "Mobiliario Perimetral",
    3: "Mesas",
    4: "Probadores",
    5: "Caja",
    6: "Dotación",
    7: "Instalaciones",
    8: "Señalética",
    9: "BOH",
};

const categoriasMapping = {
    TiendasTop: "Tiendas Top",
    TiendasLight: "Tiendas Light",
    TiendasLow: "Tiendas Low",
    General: "General",
};

const columnsConEstado = [
    {
        key: "rutaImagen",
        label: "Imagen",
        render: (row) => (
            <img
                src={row.rutaImagen}
                alt={row.nombreBook}
                style={{ width: "40px", height: "40px" }}
            />
        ),
    },
    { key: "nombreBook", label: "Nombre" },
    { key: "referencia", label: "Referencia" },
    {
        key: "nombreFamilia",
        label: "Familia",
        render: (row) => familiaMapping[row.codFamilia] || "N/A",
    },
    {
        key: "codCategorias",
        label: "Categorías",
        render: (row) => {
            // Si el array de categorías está presente, simplemente lo unes con comas
            if (Array.isArray(row.categorias) && row.categorias.length > 0) {
                return row.categorias
                    .map(
                        (categoria) => categoriasMapping[categoria] || categoria
                    )
                    .join(", "); // Une los nombres de las categorías
            } else {
                return "Sin categorías"; // Valor por defecto si no hay categorías
            }
        },
    },
    {
        key: "estado",
        label: "Estado",
        render: (row) => {
            let variant;
            const estadoLegible = estadoLabels[row.estado] || "Sin estado";

            switch (row.estado) {
                case "Publicado":
                    variant = "success";
                    break;
                case "EnEdicion":
                    variant = "warning";
                    break;
                case "Archivado":
                    variant = "info";
                    break;
                case "PendienteDeAprobacion":
                    variant = "info";
                    break;
                case "ListoParaPublicar":
                    variant = "success";
                    break;
                default:
                    variant = "default";
            }

            return <Badge label={estadoLegible} variant={variant} />;
        },
    },
];

const columnsConEstadoModificacion = [
    {
        key: "rutaImagen",
        label: "Imagen",
        render: (row) => (
            <img
                src={row.rutaImagen}
                alt={row.nombreBook}
                style={{ width: "40px", height: "40px" }}
            />
        ),
    },
    { key: "nombreBook", label: "Nombre" },
    { key: "referencia", label: "Referencia" },
    { key: "nombreFamilia", label: "Familia" },
    {
        key: "codCategorias",
        label: "Categorías",
        render: (row) =>
            Array.isArray(row.codCategorias)
                ? row.codCategorias.join(", ")
                : "Sin categorías",
    },
    {
        key: "estadoModificacion",
        label: "Estado de Modificación",
        render: (row) => {
            let variant;
            const estadoModificacionLegible =
                estadoModificacionLabels[row.estadoModificacion] ||
                "Sin estado";

            switch (row.estadoModificacion) {
                case "Pendiente":
                    variant = "info";
                    break;
                case "Modificado":
                    variant = "warning";
                    break;
                case "Descatalogado":
                    variant = "danger";
                    break;
                case "Nuevo":
                    variant = "success";
                    break;
                default:
                    variant = "default";
            }

            return (
                <Badge label={estadoModificacionLegible} variant={variant} />
            );
        },
    },
];

const columnsSinEstado = [
    {
        key: "rutaImagen",
        label: "Imagen",
        render: (row) => (
            <img
                src={row.rutaImagen}
                alt={row.nombreBook}
                style={{ width: "40px", height: "40px" }}
            />
        ),
    },
    { key: "nombreBook", label: "Nombre" },
    { key: "referencia", label: "Referencia" },
    {
        key: "nombreFamilia",
        label: "Familia",
        render: (row) => familiaMapping[row.codFamilia] || "N/A",
    },
    {
        key: "tipoCambio",
        label: "Tipo de Cambio",
        render: (row) => row.tipoCambio || "N/A",
    },
];

const columnsConTipoDeCambio = [
    {
        key: "rutaImagen",
        label: "Imagen",
        render: (row) => (
            <img
                src={row.rutaImagen}
                alt={row.nombreBook}
                style={{ width: "40px", height: "40px" }}
            />
        ),
    },
    { key: "nombreBook", label: "Nombre" },
    { key: "referencia", label: "Referencia" },
    { key: "nombreFamilia", label: "Familia" },
    {
        key: "codCategorias",
        label: "Categorías",
        render: (row) =>
            Array.isArray(row.codCategorias)
                ? row.codCategorias.join(", ")
                : "Sin categorías",
    },
    { key: "codEstadoActual", label: "Estado" },
    {
        key: "tipoCambio",
        label: "Tipo de Cambio",
        render: (row) => row.tipoCambio || "N/A",
    },
];

const columnsSinTipoDeCambio = [
    {
        key: "rutaImagen",
        label: "Imagen",
        render: (row) => (
            <img
                src={row.rutaImagen}
                alt={row.nombreBook}
                style={{ width: "40px", height: "40px" }}
            />
        ),
    },
    { key: "nombreBook", label: "Nombre" },
    { key: "referencia", label: "Referencia" },
    {
        key: "codFamilias",
        label: "Familia",
        render: (row) => familiaMapping[row.codFamilia] || "N/A",
    },
    {
        key: "categorias",
        label: "Categorías",
        render: (row) => {
            // Si el array de categorías está presente, simplemente lo unes con comas
            if (Array.isArray(row.categorias) && row.categorias.length > 0) {
                return row.categorias
                    .map(
                        (categoria) => categoriasMapping[categoria] || categoria
                    )
                    .join(", "); // Une los nombres de las categorías
            } else {
                return "Sin categorías"; // Valor por defecto si no hay categorías
            }
        },
    },
    {
        key: "estadoModificacion",
        label: "Estado",
        render: (row) => {
            let variant;
            const estadoModificacionLegible =
                estadoModificacionLabels[row.estadoModificacion] ||
                "Sin estado";

            switch (row.estadoModificacion) {
                case 1: // Pendiente
                    variant = "info";
                    break;
                case 2: // Modificado
                    variant = "warning";
                    break;
                case 3: // Descatalogado
                    variant = "danger";
                    break;
                case 4: // Nuevo
                    variant = "success";
                    break;
                default:
                    variant = "default";
            }

            return (
                <Badge label={estadoModificacionLegible} variant={variant} />
            );
        },
    },
];

export default function Versiones() {
    const [activeTab, setActiveTab] = useState("borrador");
    const [muebles, setMuebles] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedMuebles, setSelectedMuebles] = useState([]);
    const [scheduledDate, setScheduledDate] = useState("");
    const [description, setDescription] = useState("");
    const [selectedVersion, setSelectedVersion] = useState("");
    const [mueblesPorVersion, setMueblesPorVersion] = useState([]);
    const [versionOptions, setVersionOptions] = useState([]);
    const [borradores, setBorradores] = useState([]);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [currentBorrador, setCurrentBorrador] = useState(null);
    const [familiaOptions, setFamiliaOptions] = useState([]);
    const [subfamiliaOptions, setSubfamiliaOptions] = useState([]);
    const [dotacionesOptions, setDotacionesOptions] = useState([]);
    const [dotacionesSeleccionadas, setDotacionesSeleccionadas] = useState([]);
    const [isEditMode, setIsEditMode] = useState(false);

    const api = getApiInstance();

    useEffect(() => {
        if (activeTab === "listoParaDesplegar") {
            fetchMueblesListos();
        } else if (activeTab === "versionesAnteriores") {
            fetchVersiones();
        } else if (activeTab === "borrador") {
            fetchBorradores();
            fetchFamilias();
            fetchSubfamilias();
            fetchDotaciones();
            console.log("Datos de borradores:", borradores);
        }
    }, [activeTab]);

    const actions = (row) => [
        {
            label: "Editar",
            onClick: () => {
                setCurrentBorrador(row); // Almacena los datos del borrador seleccionado
                setIsEditMode(true); // Establece el estado a modo de edición
                setIsEditModalOpen(true); // Abre el modal de edición
            },
            icon: <FiEdit />,
        },
        {
            label: "Eliminar",
            onClick: () => handleEliminarMueble(row.codMueble),
            icon: <FiTrash />,
        },
    ];

    const handleEliminarMueble = async (muebleId) => {
        try {
            const response = await api.patch(
                `/api/Mueble/ActualizarEstadoModificacion/${muebleId}`,
                JSON.stringify(3),
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                alert("Estado de modificación actualizado a 'Descatalogado'.");
                await fetchBorradores();
            } else {
                alert(
                    "Hubo un problema al actualizar el estado de modificación."
                );
            }
        } catch (error) {
            console.error(
                "Error al actualizar el estado de modificación:",
                error
            );
            alert("Error al actualizar el estado de modificación.");
        }
    };

    const handleDateChange = (date) => {
        const localDate = new Date(date);
        const utcDate = new Date(
            localDate.getTime() - localDate.getTimezoneOffset() * 60000
        ); //convertir a UTC
        setScheduledDate(utcDate.toISOString().slice(0, 16)); //formato 'yyyy-MM-ddTHH:mm'
    };

    const fetchBorradores = async () => {
        try {
            const response = await api.get("/api/Mueble/GetBorradores");
            setBorradores(response.data);
        } catch (error) {
            console.error("Error al obtener los borradores:", error);
        }
    };

    const fetchMueblesListos = async () => {
        try {
            const response = await api.get("/api/Mueble/GetListosParaPublicar");
            console.log("Muebles recibidos:", response.data);

            if (response.data && Array.isArray(response.data)) {
                setMuebles(response.data);
            } else {
                setMuebles([]);
                console.warn("Datos inválidos recibidos");
            }
        } catch (error) {
            console.error(
                "Error fetching muebles listos para desplegar",
                error
            );
            setMuebles([]);
        }
    };

    const fetchVersiones = async () => {
        try {
            const response = await api.get("/api/Book/GetAllBooks");

            const versions = response.data.map((book) => ({
                label: `Versión v.${book.version}`,
                value: book.codBook,
            }));

            setVersionOptions(versions);
        } catch (error) {
            console.error("Error al obtener las versiones", error);
        }
    };

    const fetchMueblesPorVersion = async (codBook) => {
        try {
            const response = await api.get(
                `/api/Mueble/GetMueblesPorVersion/${codBook}`
            );
            setMueblesPorVersion(response.data);
        } catch (error) {
            console.error(
                "Error al obtener los muebles con cambios por versión",
                error
            );
            setMueblesPorVersion([]);
        }
    };

    const handleVersionSelect = (option) => {
        setSelectedVersion(option.value);
        fetchMueblesPorVersion(option.value);
    };

    const handleProgramarDespliegue = () => {
        setIsModalOpen(true);
    };

    const handleModalAccept = async () => {
        if (!scheduledDate || selectedMuebles.length === 0) {
            alert("Selecciona al menos un mueble y una fecha.");
            return;
        }

        try {
            const requestBody = {
                codMuebles: selectedMuebles,
                fechaPublicacion: scheduledDate,
            };

            const response = await api.patch(
                "/api/Mueble/ProgramarDespliegue",
                requestBody
            );
            console.log(
                "Programación de despliegue completada:",
                response.data
            );
            setIsModalOpen(false);
        } catch (error) {
            console.error("Error al programar el despliegue", error);
            alert(
                "Error al programar el despliegue. Revisa la consola para más detalles."
            );
        }
    };

    const handleSelectMueble = (muebleId) => {
        setSelectedMuebles((prevSelectedMuebles) => {
            const isSelected = prevSelectedMuebles.includes(muebleId);
            const newSelectedMuebles = isSelected
                ? prevSelectedMuebles.filter((id) => id !== muebleId)
                : [...prevSelectedMuebles, muebleId];

            console.log("Muebles seleccionados:", newSelectedMuebles);
            return newSelectedMuebles;
        });
    };
    const handleDesplegarVersion = async () => {
        if (selectedMuebles.length === 0) {
            alert("Selecciona al menos un mueble para desplegar.");
            return;
        }

        console.log("Muebles seleccionados para desplegar:", selectedMuebles);

        try {
            // Enviar el array de IDs de muebles
            const requestBody = selectedMuebles;

            const response = await api.patch(
                "/api/Mueble/PublicarMuebles",
                requestBody
            );
            console.log("Muebles desplegados correctamente:", response);

            // Volver a cargar la lista de muebles listos para desplegar
            await fetchMueblesListos();

            // Limpiar la selección de muebles
            setSelectedMuebles([]);

            alert("Muebles desplegados correctamente.");
        } catch (error) {
            console.error("Error al desplegar los muebles", error);
            alert(
                "Error al desplegar los muebles. Revisa la consola para más detalles."
            );
        }
    };

    const enviarABorradores = async (codMueble) => {
        try {
            const response = await api.patch(
                `/api/Mueble/EnviarABorradores/${codMueble}`
            );
            console.log("Mueble enviado a borradores", response.data);
            alert("Mueble enviado a borradores");
            await fetchBorradores();
        } catch (error) {
            console.error("Error al enviar mueble a borradores", error);
            alert("Hubo un error al enviar el mueble a borradores.");
        }
    };

    const handleListoParaDesplegar = async () => {
        if (selectedMuebles.length === 0) {
            alert(
                "Selecciona al menos un mueble para cambiar su estado a 'Listo para desplegar'."
            );
            return;
        }

        try {
            const requestBody = {
                codMuebles: selectedMuebles,
            };

            const api = getApiInstance();
            const response = await api.patch(
                "/api/Mueble/CambiarEstadoAListoParaDesplegar",
                requestBody
            );

            if (response.status === 200) {
                alert(
                    "Muebles cambiados a 'Listo para desplegar' correctamente."
                );
                await fetchBorradores();
            } else {
                alert("Hubo un problema al cambiar el estado de los muebles.");
            }
        } catch (error) {
            console.error("Error al cambiar el estado de los muebles:", error);
            alert("Error al cambiar el estado de los muebles.");
        }
    };

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => {
        setIsEditModalOpen(false); // Cierra el modal
        setCurrentBorrador(null); // Limpia el borrador seleccionado
    };
    const fetchFamilias = async () => {
        try {
            const response = await api.get("/api/Familia/GetAllFamilias");
            const familias = response.data.map((familia) => ({
                label: familia.nombreFamilia,
                value: familia.codFamilia,
            }));
            setFamiliaOptions(familias);
        } catch (error) {
            console.error("Error al obtener las familias:", error);
        }
    };
    const fetchSubfamilias = async () => {
        try {
            const response = await api.get("/api/Categoria/GetSubfamilias");
            const subfamilias = response.data.map((subfamilia) => ({
                label: subfamilia.nombreSubfamilia,
                value: subfamilia.codCategoria,
            }));
            setSubfamiliaOptions(subfamilias);
        } catch (error) {
            console.error("Error al obtener las subfamilias:", error);
        }
    };

    const handleSelectFamilia = (selectedOption) => {
        console.log("Familia seleccionada:", selectedOption);
        setCurrentBorrador((prev) => ({
            ...prev,
            codFamilia: selectedOption.value,
        }));
    };

    const handleSelectSubfamilia = (selectedOption) => {
        console.log("Subfamilia seleccionada:", selectedOption);
        setCurrentBorrador((prev) => ({
            ...prev,
            codSubfamilia: selectedOption.value,
        }));
    };

    const fetchMueblesPendientesDeAprobacion = async () => {
        try {
            const response = await api.get(
                "/api/Mueble/GetMueblesPendientesDeAprobacion"
            );
            setMuebles(response.data);
        } catch (error) {
            console.error(
                "Error al obtener los muebles pendientes de aprobación:",
                error
            );
        }
    };

    const fetchDotaciones = async () => {
        try {
            const response = await api.get("/api/Dotacion/GetAllDotaciones");
            const dotaciones = response.data.map((dotacion) => ({
                label: dotacion.nombreDotacion,
                value: dotacion.codDotacion,
            }));
            setDotacionesOptions(dotaciones);
            console.log("Dotaciones recibidas:", dotaciones);
        } catch (error) {
            console.error("Error al obtener las dotaciones:", error);
        }
    };

    const handleSelectDotacion = (selectedOption) => {
        const newDotacion = {
            codDotacion: selectedOption.value,
            unidades: 1,
        };

        setDotacionesSeleccionadas((prev) => [...prev, newDotacion]);
    };
    const asignarDotaciones = async () => {
        try {
            const requestBody = dotacionesSeleccionadas;

            const response = await api.post(
                `/api/Mueble/AsignarDotacionesAMueble?codMueble=${currentBorrador.codMueble}`,
                requestBody
            );

            if (response.status === 200) {
                alert("Dotaciones asignadas correctamente.");
                setDotacionesSeleccionadas([]);
            } else {
                alert("Hubo un problema al asignar las dotaciones.");
            }
        } catch (error) {
            console.error("Error al asignar las dotaciones:", error);
            alert("Error al asignar las dotaciones.");
        }
    };

    const handleSaveChanges = async () => {
        try {
            const requestBody = {
                codMueble: isEditMode ? currentBorrador.codMueble : undefined, // Solo agregar codMueble en modo edición
                codFamilia: currentBorrador.codFamilia,
                referencia: currentBorrador.referencia,
                nombreBook: currentBorrador.nombreBook,
                nombreFamilia: currentBorrador.nombreFamilia || null,
                rutaPlano: currentBorrador.rutaPlano,
                rutaImagen: currentBorrador.rutaImagen,
                codEstadoActual: currentBorrador.codEstadoActual,
                fechaPublicacion: currentBorrador.fechaPublicacion,
                pendienteDeAprobacion: currentBorrador.pendienteDeAprobacion,
                descripcion: currentBorrador.descripcion || null, // Descripción opcional
                categorias: currentBorrador.categorias,
                codEstadoModificacion: isEditMode ? 2 : 4, // Estado "Modificado" o "Nuevo"
            };

            let response;
            if (isEditMode) {
                response = await api.put(
                    `/api/Mueble/${currentBorrador.codMueble}`,
                    requestBody
                );
            } else {
                response = await api.post(`/api/Mueble`, requestBody);
            }

            if (response.status === 200) {
                alert(
                    isEditMode
                        ? "Mueble actualizado correctamente."
                        : "Mueble creado correctamente."
                );

                // Actualiza la tabla llamando a la función correspondiente según la pestaña activa
                if (activeTab === "borrador") {
                    await fetchBorradores(); // Refrescar la tabla de borradores
                } else if (activeTab === "listoParaDesplegar") {
                    await fetchMueblesListos(); // Refrescar la tabla de muebles listos para desplegar
                }

                setIsEditModalOpen(false); // Cierra el modal después de guardar
            } else {
                alert("Hubo un error al guardar los cambios.");
            }
        } catch (error) {
            console.error("Error al guardar los cambios:", error);
            alert("Hubo un error al guardar el mueble.");
        }
    };

    const handleSelectCategoria = (selectedOption) => {
        setCurrentBorrador((prev) => {
            return {
                ...prev,
                categorias: [selectedOption.value], // Solo mantiene la categoría seleccionada
            };
        });
    };

    const openCreateModal = () => {
        setIsEditMode(false);
        setCurrentBorrador({
            // Limpia el estado del borrador para empezar desde cero
            codFamilia: "",
            referencia: "",
            nombreBook: "",
            nombreFamilia: "",
            rutaPlano: "",
            rutaImagen: "",
            codEstadoActual: 1, // Estado inicial 'En edición' siempre que se crea un nuevo mueble
            pendienteDeAprobacion: false,
            descripcion: "",
            categorias: [],
            codEstadoModificacion: 4, // Estado inicial 'Nuevo'
        });
        setIsEditModalOpen(true);
    };

    return (
        <div className="versiones">
            <SectionHeader
                title="Historial de versiones"
                subtitle="Crea y gestiona borradores, despliega versiones y consulta los cambios realizados anteriormente"
            />
            <Tabs
                tabs={[
                    { id: "borrador", label: "Borrador" },
                    { id: "listoParaDesplegar", label: "Lista para desplegar" },
                    {
                        id: "versionesAnteriores",
                        label: "Versiones anteriores",
                    },
                ]}
                activeTab={activeTab}
                onTabChange={setActiveTab}
            />
            <div className="versiones__content">
                {activeTab === "borrador" && (
                    <>
                        <div className="versiones__content__header-borrador">
                            <div className="versiones__content__header-borrador__buttons">
                                <Button
                                    label="Listo para desplegar"
                                    icon={<PiRocket />}
                                    variant="outline"
                                    onClick={handleListoParaDesplegar}
                                />
                                <Button
                                    label="Crear mueble"
                                    icon={<IoAdd />}
                                    variant="filled"
                                    onClick={openCreateModal}
                                />
                            </div>
                        </div>
                        <Table
                            columns={columnsSinTipoDeCambio}
                            data={borradores || []}
                            actions={actions}
                            noResultsMessage="No se han encontrado resultados."
                            onRowSelect={handleSelectMueble}
                            selectedRows={selectedMuebles}
                        />
                    </>
                )}
                {activeTab === "listoParaDesplegar" && (
                    <>
                        <div className="versiones__content__header-listo-para-desplegar">
                            <div className="versiones__content__header-listo-para-desplegar__button">
                                <Button
                                    label="Programar despliegue"
                                    icon={<PiClock />}
                                    variant="outline"
                                    onClick={handleProgramarDespliegue}
                                />
                                <Button
                                    label="Desplegar versión"
                                    icon={<PiRocket />}
                                    variant="filled"
                                    onClick={handleDesplegarVersion}
                                />
                            </div>
                        </div>
                        <Table
                            columns={columnsConEstado}
                            data={muebles || []}
                            noResultsMessage="No se han encontrado resultados."
                            selectableRows={true}
                            selectedRows={selectedMuebles}
                            onRowSelect={handleSelectMueble}
                        />
                    </>
                )}
                {activeTab === "versionesAnteriores" && (
                    <>
                        <div className="versiones__content__header-versiones-anteriores">
                            <h3 className="versiones__content__header-versiones-anteriores__title">
                                {selectedVersion
                                    ? `Versión v.${selectedVersion}`
                                    : "Selecciona una versión"}
                            </h3>
                                <InputDropdown
                                    options={versionOptions}
                                    onSelect={handleVersionSelect}
                                    placeholder="Selecciona una versión"
                                    showLabel={false}
                                />
                        </div>
                        <Table
                            columns={columnsSinEstado}
                            data={mueblesPorVersion || []}
                            noResultsMessage="No se han encontrado muebles para la versión seleccionada."
                        />
                    </>
                )}
            </div>

            <Modal
                title="Programar Despliegue"
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onAccept={handleModalAccept}
                outlineButtonLabel="Cancelar"
                primaryButtonLabel="Aceptar"
            >
                <form className="versiones__modal__form">
                    <div className="versiones__modal__form-group">
                        <InputDate
                            label="Fecha de despliegue"
                            value={scheduledDate}
                            onChange={handleDateChange}
                        />
                    </div>
                    <div className="versiones__modal__form-group">
                        <InputTextfield
                            label="Mensaje de despliegue"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Describe los cambios..."
                        />
                    </div>
                </form>
            </Modal>
            <Modal
                title={isEditMode ? "Editar mueble" : "Crear mueble"}
                isOpen={isEditModalOpen}
                onClose={closeModal}
                onCancel={closeModal}
                onAccept={handleSaveChanges}
            >
                <div className="dashboard-productos__modal">
                    <form className="dashboard-productos__modal__content">
                        <Input
                            type="text"
                            label="Nombre"
                            name="nombre"
                            value={currentBorrador?.nombreBook || ""}
                            onChange={(e) =>
                                setCurrentBorrador({
                                    ...currentBorrador,
                                    nombreBook: e.target.value,
                                })
                            }
                            placeholder="Introduce el nombre del artículo"
                        />
                        <Input
                            type="text"
                            label="Referencia"
                            name="referencia"
                            value={currentBorrador?.referencia || ""}
                            onChange={(e) =>
                                setCurrentBorrador({
                                    ...currentBorrador,
                                    referencia: e.target.value,
                                })
                            }
                            placeholder="Introduce la referencia del artículo"
                        />
                        <InputDropdown
                            label="Familia"
                            options={familiaOptions}
                            onSelect={handleSelectFamilia}
                            placeholder="Selecciona una familia"
                        />
                        <InputDropdown
                            label="Subfamilia"
                            options={subfamiliaOptions}
                            onSelect={handleSelectCategoria}
                            placeholder="Selecciona una subfamilia"
                        />
                        <Input
                            type="text"
                            label="Tipo/Variante"
                            name="tipo"
                            value={currentBorrador?.tipo || ""}
                            placeholder="Introduce el tipo de artículo"
                        />
                        <Input
                            type="file"
                            label="Foto del artículo"
                            name="fotoArticulo"
                        />
                        <Input
                            type="file"
                            label="Archivo PDF del artículo"
                            name="archivoPdf"
                        />
                        <Input
                            type="file"
                            label="Archivo DWG/DXF del artículo"
                            name="archivoDwg"
                        />
                        <Input
                            type="textarea"
                            label="Descripción del artículo"
                            name="descripcion"
                            value={currentBorrador?.descripcion || ""}
                            onChange={(e) =>
                                setCurrentBorrador({
                                    ...currentBorrador,
                                    descripcion: e.target.value,
                                })
                            }
                            placeholder="Escribe una breve descripción del artículo (opcional)"
                        />
                        <Input
                            type="checkbox"
                            label="Pendiente de aprobación"
                            name="pendienteDeAprobacion"
                            checked={
                                currentBorrador?.pendienteDeAprobacion || false
                            }
                            onChange={(e) =>
                                setCurrentBorrador({
                                    ...currentBorrador,
                                    pendienteDeAprobacion: e.target.checked,
                                })
                            }
                        />
                        <InputDropdown
                            label="Dotaciones"
                            options={dotacionesOptions}
                            onSelect={handleSelectDotacion}
                            placeholder="Selecciona una dotación"
                        />
                        <Button
                            variant="filled"
                            label="Asignar dotaciones"
                            onClick={asignarDotaciones}
                            fullWidth={true}
                        />
                    </form>
                </div>
            </Modal>
        </div>
    );
}
